import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactMarkdown from 'react-markdown';

import { selectProfile as selectStudentProfile } from '../../redux/profileSlice';
import {
  selectActiveActivity,
  selectActiveCourse,
  addMessageToCurrentActivity,
} from '../../redux/classroomSlice';
import { selectContentByActivityId } from '../../redux/contentSlice';
import { ProfessorService } from '../../services/llmClient/ProfessorService';
import LoadingSpinner from '../ui/LoadingSpinner';
import '../shared/ActivityComponent.css';

const Professor = () => {
  const dispatch = useDispatch();
  const activeActivity = useSelector(selectActiveActivity);
  const activeCourse = useSelector(selectActiveCourse);
  const profile = useSelector(selectStudentProfile);

  const isActive = activeActivity?.leader === 'Professor';
  const activityId = activeActivity?.id;
  const notes = useSelector((state) => selectContentByActivityId(state, activityId));
  const chatHistory = useMemo(() => activeActivity?.messages || [], [activeActivity]);
  const prevMessageCount = useRef(chatHistory.length);

  // 🧠 Memoized response handler
  const sendResponse = useCallback(async () => {
    const lastMessage = chatHistory[chatHistory.length - 1];
    if (!lastMessage || !activeActivity?.title) return;

    try {
      const response = await ProfessorService.processStudentMessage(
        activityId,
        activeActivity.title,
        activeActivity.leader,
        lastMessage.text,
        profile,
        activeCourse,
        notes,
        chatHistory
      );

      if (response) {
        dispatch(addMessageToCurrentActivity({ message: response }));
      }
    } catch (error) {
      dispatch(
        addMessageToCurrentActivity({
          message: {
            sender: 'Professor',
            text: "I'm having trouble processing your question right now.",
            timestamp: new Date().toLocaleTimeString(),
          },
        })
      );
    }
  }, [
    activityId,
    activeActivity?.title,
    activeActivity?.leader,
    chatHistory,
    profile,
    activeCourse,
    notes,
    dispatch,
  ]);
  // Notes should already be prefetched and stored in Redux
  useEffect(() => {
    if (!isActive || !activityId) return;

    if (!notes) {
      // eslint-disable-next-line no-console
      console.warn(`⚠️ Missing notes for activity ${activityId}`);
    }
  }, [isActive, notes, activityId]);

  // 🤖 Respond when student sends a message
  useEffect(() => {
    if (!isActive) return;

    const currentCount = chatHistory.length;
    if (currentCount > prevMessageCount.current) {
      const lastMessage = chatHistory[currentCount - 1];
      if (lastMessage?.sender === 'Student') {
        sendResponse();
      }
    }

    prevMessageCount.current = currentCount;
  }, [chatHistory, isActive, sendResponse]);

  if (!isActive) return null;

  const loading = !notes;

  return (
    <div className="activity-component">
      <div className="activity-content">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <ReactMarkdown
            components={{
              code: ({ inline, children, ...props }) =>
                inline ? (
                  <code {...props} className="inline-code">
                    {children}
                  </code>
                ) : (
                  <pre className="code-block" {...props}>
                    {children}
                  </pre>
                ),
              p: ({ children, ...props }) => {
                // If the only child is a code block, return children directly (avoid <p><pre></pre></p>)
                if (
                  children &&
                  React.Children.count(children) === 1 &&
                  children[0]?.type === 'pre'
                ) {
                  return children;
                }
                return <p {...props}>{children}</p>;
              },
            }}
          >
            {typeof notes === 'string'
              ? notes
              : Array.isArray(notes)
                ? notes.map((n) => (typeof n === 'string' ? n : n?.text || String(n))).join('\n')
                : String(notes)}
          </ReactMarkdown>
        )}
      </div>

      {activeActivity?.title && (
        <p style={{ marginTop: '20px', fontStyle: 'italic' }}>Teaching: {activeActivity.title}</p>
      )}
    </div>
  );
};

export default Professor;
